import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import { useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { addToShoppingcart, removeFromShoppingcart } from "../../redux/actions";
import { AppState, ProductType, SubCategoryType } from "../../redux/types";
import { classNames } from "../../utils/classnames";
import { getAbsoluteURL } from "../../utils/url-helper";
import ReadMoreBlocks from "../ReadMoreBlocks";
import styles from "./product.module.css";

interface ProductLabels {
    close: string;
    notAvailable: string;
    readMore: string;
}

const defaultLabels: ProductLabels = {
    close: "Sulje",
    notAvailable: "Not available in your region",
    readMore: "Lue lisää",
};

const getFlagCountryCode = (regionCode: string) => {
    if (regionCode === "UK") return "GB";
    return regionCode;
};

const Product = ({
    product,
    optional,
    single,
    labels = defaultLabels,
    interactionLocked = false,
}: {
    product: ProductType;
    optional: boolean;
    single: boolean;
    labels?: ProductLabels;
    interactionLocked?: boolean;
}) => {
    const dispatch = useDispatch();
    const selectedProducts = useSelector((state: AppState) => state.cart.inCart);
    const [activeIndex, setActiveIndex] = useState(0);
    const images = product.images ?? [];
    const inBasket = selectedProducts.find((p) => p?.id === product.id) ? true : false;

    const addToShoppingcartHandler = () => {
        if (interactionLocked) return;
        if (!inBasket) {
            if (single) {
                selectedProducts.forEach((p) => {
                    const _p = p as ProductType;
                    if (!_p.locked && _p.category === product.category) {
                        dispatch(removeFromShoppingcart(_p.id));
                    }
                });
            }
            dispatch(addToShoppingcart(product));
        }
    };

    const removeFromShoppingcartHandler = () => {
        if (interactionLocked) return;
        dispatch(removeFromShoppingcart(product.id));
    };

    const selected = inBasket;
    const active = {
        first: product.emphasized || false,
        second: optional && !selected,
        third: selected || product.locked || false,
    };

    return (
        <div className={classNames(styles.productContainer, { [styles.unavailableProduct]: product.isAvailable === false })}>
            <div className={styles.productMeta}>
                {product.showNameLine && <h5 className={styles.productName}>{product.name}</h5>}
                {product.regions && product.regions.length > 0 && (
                    <span className={styles.productFlags} aria-label="Available countries">
                        {product.regions.map((region) => (
                            <ReactCountryFlag
                                key={region}
                                countryCode={getFlagCountryCode(region)}
                                svg
                                className={styles.productFlag}
                                aria-label={region}
                            />
                        ))}
                    </span>
                )}
            </div>
            <div className={styles.boxesContainer}>
                    {images.length > 0 ? (
                        <div className={classNames(styles.boxContent, styles.sliderContent, { [styles.active]: active.first, [styles.locked]: active.first })}>
                            {images.length > 1 && (
                                <button
                                    className={classNames(styles.sliderBtn, styles.sliderBtnPrev)}
                                    onClick={(e) => { e.stopPropagation(); setActiveIndex((activeIndex - 1 + images.length) % images.length); }}
                                    aria-label="Previous image"
                                ><FiChevronLeft /></button>
                            )}
                            <img src={getAbsoluteURL(images[activeIndex])} alt="" className={styles.carouselImage} />
                            {images.length > 1 && (
                                <button
                                    className={classNames(styles.sliderBtn, styles.sliderBtnNext)}
                                    onClick={(e) => { e.stopPropagation(); setActiveIndex((activeIndex + 1) % images.length); }}
                                    aria-label="Next image"
                                ><FiChevronRight /></button>
                            )}
                        </div>
                    ) : (
                        <div className={classNames(styles.boxContent, { [styles.active]: active.first, [styles.locked]: active.first })}>
                            {product.firstBox?.[0]?.__component === "boxes.image" ? (
                                <img src={getAbsoluteURL(product.firstBox[0].Image.data.attributes.url)} alt="" className={styles.productImage} />
                            ) : product.firstBox?.[0]?.Content ? (
                                <BlocksRenderer content={product.firstBox[0].Content} />
                            ) : null}
                        </div>
                    )}
                <div
                    className={styles.box}
                    onClick={() => {
                        if (optional) {
                            removeFromShoppingcartHandler();
                        }
                    }}
                >
                    <div className={classNames(styles.boxContent, { [styles.active]: active.second })}>
                        <ReadMoreBlocks content={product.secondBox} initialBlockCount={2} labels={labels} />
                    </div>
                </div>
                <div className={styles.box} onClick={product.isAvailable === false || interactionLocked ? undefined : addToShoppingcartHandler}>
                    {product.isAvailable === false ? (
                        <div className={classNames(styles.boxContent, styles.unavailableBox)}>
                            <p>{labels.notAvailable}</p>
                        </div>
                    ) : (
                        <div className={classNames(styles.boxContent, styles.thirdBox, { [styles.active]: active.third, [styles.locked]: product.locked || false })}>
                            <BlocksRenderer content={product.thirdBox} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const ProductHeader = ({ category }: { category: SubCategoryType }) => {
    return (
        <div className={styles.header}>
            <h2 className={styles.title}>{category.attributes.Name}</h2>
            <div className={styles.descriptionContainer}>
                <BlocksRenderer content={category.attributes.Description || []} />
            </div>
        </div>
    );
};

export default Product;
